import { useCallback, useContext, useRef } from 'react';

import { match, P } from 'ts-pattern';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { type AIEventsInstrumentationConfig, AIEventsInstrumentationContext } from '../context';
import { defaultAIEventsInstrumentationConfigValues } from '../context/model';
import { getTagsWithPlatformTag } from '../effects/getTagsWithPlatformTag';
import {
	type AIEventBaseAttributes,
	type AIEventErrorAttributes,
	type AIEventPayload,
	type AIFeedbackResultType,
	type AIResultViewedPayload,
	AISessionState,
	type InternalAIBaseEventPayload,
	type InternalAIEvent,
	type OverrideAIEventPayload,
} from '../events';

import { useAIInstrumentationSetup } from './useAIInstrumentationSetup';

export const useAIEventsInstrumentation = (customConfig?: AIEventsInstrumentationConfig) => {
	let context = useContext(AIEventsInstrumentationContext);
	const aiEventAttributesOverride = useRef<Partial<AIEventBaseAttributes>>({});

	if (customConfig) {
		context = {
			config: {
				...defaultAIEventsInstrumentationConfigValues,
				...customConfig,
			},
			// eslint-disable-next-line react-hooks/rules-of-hooks
			...useAIInstrumentationSetup(
				customConfig.singleInstrumentationID,
				customConfig.cookieSingleInstrumentationConfig,
			),
		};
	}
	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (context === null) {
		throw new Error(
			'useAIEventsInstrumentation must be used within a AIEventsInstrumentationProvider',
		);
	}

	const {
		config: {
			source,
			aiExperienceName,
			aiFeatureName,
			proactiveGeneratedAI,
			userGeneratedAI,
			isAIFeature,
			channel,
			subproduct,
			actionSubjectId,
			tags,
			product,
			eventDispatcherSource,
			skipAISessionValidation,
			customAnalyticsFire,
			additionalAttributes,
			useDefaultGASv3EventSchema,
			enablePlatformEvents,
		},
		refreshSingleInstrumentationID,
		getSingleInstrumentationID,
		setAISessionState,
		getAISessionState,
	} = context;

	const validateAndUpdateAISession = useCallback(
		(nextState: AISessionState): boolean => {
			const currentAISessionState = getAISessionState();

			return match({ currentState: currentAISessionState, nextState, skipAISessionValidation })
				.with({ skipAISessionValidation: true }, () => true)
				.with(
					{
						currentState: AISessionState.unset,
						nextState: P.union(
							AISessionState.viewed,
							AISessionState.error,
							AISessionState.dismissed,
						),
					},
					() => {
						// eslint-disable-next-line no-console
						console.error(
							`[@atlassian/ai-analytics]: ${nextState} event should be called only after trackAIInteractionInit()`,
						);
						return false;
					},
				)
				.with(
					{
						currentState: AISessionState.initiated,
						nextState: P.union(
							AISessionState.submitted,
							AISessionState.actioned,
							AISessionState.discarded,
						),
					},
					() => {
						// eslint-disable-next-line no-console
						console.error(
							`[@atlassian/ai-analytics]: ${nextState} event should be called only after trackAIResultView()`,
						);
						return false;
					},
				)
				.otherwise(() => {
					setAISessionState(nextState);
					return true;
				});
		},
		[setAISessionState, getAISessionState, skipAISessionValidation],
	);

	const fireEventWithOptionalChannel = useCallback(
		(event: InternalAIEvent) => {
			if (typeof customAnalyticsFire === 'function') {
				customAnalyticsFire(event, createAnalyticsEvent({}));
				return;
			}

			if (enablePlatformEvents) {
				// it goes thru the aiMate analytics listener to avoid validation per prodcut listener
				// https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/f904f690cf57ef84acad6c7bf1b5bd90ad9cada2/analytics/analytics-listeners/src/atlaskit/process-event.ts#lines-58
				createAnalyticsEvent(event).fire(FabricChannel.aiMate);
				return;
			}

			if (channel) {
				createAnalyticsEvent(event).fire(channel);
				return;
			}

			createAnalyticsEvent(event).fire();
		},
		[channel, customAnalyticsFire, createAnalyticsEvent, enablePlatformEvents],
	);

	const createAndMergeEventPayload = useCallback(
		(
			payload: OverrideAIEventPayload,
			partialPayload?: Partial<AIEventPayload>,
		): InternalAIEvent => {
			const singleInstrumentationID = getSingleInstrumentationID();

			const eventPayload: InternalAIBaseEventPayload = {
				action: payload.action,
				actionSubject: payload.actionSubject,
				actionSubjectId: partialPayload?.actionSubjectId ?? actionSubjectId,
				attributes: {
					aiExperienceName,
					aiFeatureName,
					proactiveGeneratedAI,
					userGeneratedAI,
					isAIFeature,
					eventDispatcherSource,
					singleInstrumentationID,
					...payload.attributes,
					...partialPayload?.attributes,
					...additionalAttributes,
					...aiEventAttributesOverride.current,
				},
			};

			return match<
				{
					channel: string | undefined;
					enablePlatformEvents: boolean | undefined;
					product: string;
					useDefaultGASv3EventSchema: boolean | undefined;
				},
				InternalAIEvent
			>({
				product,
				channel,
				useDefaultGASv3EventSchema,
				enablePlatformEvents,
			})
				.with({ enablePlatformEvents: true }, () => {
					return {
						eventType: 'track',
						...eventPayload,
						tags: getTagsWithPlatformTag(tags),
						source: partialPayload?.source ?? source,
						subproduct,
						product,
					};
				})
				.with(
					{ product: 'confluence', channel: P.nullish, useDefaultGASv3EventSchema: false },
					() => {
						// confluence event validation fronted logic
						// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/confluence/next/packages/analytics/src/analyticsListener.tsx#lines-53
						return {
							type: 'sendTrackEvent',
							data: {
								...eventPayload,
								tags: partialPayload?.tags ?? tags,
								source: partialPayload?.source ?? source,
								subproduct,
							},
						};
					},
				)
				.with({ product: 'jira', channel: P.nullish, useDefaultGASv3EventSchema: false }, () => {
					// jira event validation fronted logic
					// https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/f8bf8ba8c7b54c6e2764d85551b3f989b69d28d8/jira/src/packages/platform/observability/product-analytics-bridge/src/utils/extract-data-from-event.tsx#lines-164
					// it requires to use no more than 4 properties on the top level of event shape for custom event fire.
					return eventPayload;
				})
				.otherwise(() => {
					// for any other product type we should use the default event shape
					// also if there is a custom event channel, we also should use the default event shape
					return {
						eventType: 'track',
						...eventPayload,
						tags: partialPayload?.tags ?? tags,
						source: partialPayload?.source ?? source,
						subproduct,
						product,
					};
				});
		},
		[
			subproduct,
			channel,
			product,
			getSingleInstrumentationID,
			useDefaultGASv3EventSchema,
			source,
			tags,
			eventDispatcherSource,
			actionSubjectId,
			additionalAttributes,
			enablePlatformEvents,
			aiExperienceName,
			aiFeatureName,
			proactiveGeneratedAI,
			userGeneratedAI,
			isAIFeature,
		],
	);

	const trackAIInteractionInit = useCallback(
		(payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.initiated)) {
				return;
			}

			refreshSingleInstrumentationID(payload?.attributes?.singleInstrumentationID);
			const event = createAndMergeEventPayload(
				{
					action: 'initiated',
					actionSubject: 'aiInteraction',
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[
			refreshSingleInstrumentationID,
			createAndMergeEventPayload,
			fireEventWithOptionalChannel,
			validateAndUpdateAISession,
		],
	);

	const trackAIResultView = useCallback(
		(payload?: Partial<AIResultViewedPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.viewed)) {
				return;
			}
			const { doesNotMeetMAUCriteria, attributes, ...payloadRest } = payload || {};

			const event = createAndMergeEventPayload(
				{
					action: 'viewed',
					actionSubject: 'aiResult',
				},
				{
					...payloadRest,
					attributes: {
						...attributes,
						doesNotMeetMAUCriteria: !!doesNotMeetMAUCriteria,
					},
				},
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const trackAIResultError = useCallback(
		(error: AIEventErrorAttributes, payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.error)) {
				return;
			}
			const event = createAndMergeEventPayload(
				{
					action: 'error',
					actionSubject: 'aiResult',
					attributes: {
						...error,
					},
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const trackAIInteractionDismiss = useCallback(
		(payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.dismissed)) {
				return;
			}
			const event = createAndMergeEventPayload(
				{
					action: 'dismissed',
					actionSubject: 'aiInteraction',
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const trackAIResultAction = useCallback(
		(aiResultAction: string, payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.actioned)) {
				return;
			}
			const event = createAndMergeEventPayload(
				{
					action: 'actioned',
					actionSubject: 'aiResult',
					attributes: {
						aiResultAction,
					},
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const trackAIResultDiscarded = useCallback(
		(aiResultAction: string, payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.discarded)) {
				return;
			}
			const event = createAndMergeEventPayload(
				{
					action: 'discarded',
					actionSubject: 'aiResult',
					attributes: {
						aiResultAction,
					},
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const trackAIFeedbackSubmit = useCallback(
		(aiFeedbackResult: AIFeedbackResultType, payload?: Partial<AIEventPayload>): void => {
			if (!validateAndUpdateAISession(AISessionState.submitted)) {
				return;
			}
			const event = createAndMergeEventPayload(
				{
					action: 'submitted',
					actionSubject: 'aiFeedback',
					attributes: {
						aiFeedbackResult,
					},
				},
				payload,
			);

			fireEventWithOptionalChannel(event);
		},
		[validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel],
	);

	const updateAIAttributes = useCallback((newAttributes: Partial<AIEventBaseAttributes>): void => {
		aiEventAttributesOverride.current = {
			...aiEventAttributesOverride.current,
			...newAttributes,
		};
	}, []);

	return {
		updateAIAttributes,
		trackAIInteractionInit,
		trackAIResultView,
		trackAIResultError,
		trackAIResultDiscarded,
		trackAIInteractionDismiss,
		trackAIResultAction,
		trackAIFeedbackSubmit,
		refreshSingleInstrumentationID,
	};
};
